// Extracted from App.tsx (ocn-S184-P0). Behavior-preserving move.
import React, { useState } from "react";
import { AlertTriangle, Check, Loader2, Upload } from "lucide-react";
import api from "../../../api";

export function ManusIngestSection({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ p0: number; p1: number; p2: number; github_issues_created: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!taskId.trim() || !markdown.trim()) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.manusIngestReport(taskId.trim(), projectId, markdown);
      setResult(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ingest failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="section-label">Deep Audit</div>
        <button
          className="btn btn-sm"
          onClick={() => { setOpen(!open); setResult(null); setError(null); }}
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-xs)' }}
        >
          <Upload size={11} /> Import Audit Report
        </button>
      </div>

      {open && (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 12 }}>
            Paste an architectural review report to recover missed webhook results.
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>
              Task ID
            </label>
            <input
              type="text"
              value={taskId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaskId(e.target.value)}
              placeholder="audit-task-id"
              className="mono"
              style={{
                width: '100%', padding: '6px 10px', fontSize: 'var(--text-sm)',
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text)',
                fontFamily: 'var(--font-mono)',
              }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>
              Report Markdown
            </label>
            <textarea
              value={markdown}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMarkdown(e.target.value)}
              placeholder="Paste audit report markdown here..."
              rows={8}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 'var(--text-sm)',
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text)',
                fontFamily: 'var(--font-mono)', resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !taskId.trim() || !markdown.trim()}
            >
              {submitting ? <><Loader2 size={12} className="spin" /> Ingesting...</> : 'Submit'}
            </button>
            <button className="btn btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>

          {result && (
            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: 'var(--green-dim, rgba(34,197,94,0.06))',
              border: '1px solid var(--green)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
              color: 'var(--green)',
            }}>
              <Check size={12} style={{ marginRight: 6 }} />
              Ingested &mdash; P0: {result.p0}, P1: {result.p1}, P2: {result.p2}, Issues created: {result.github_issues_created}
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: 'var(--red-dim, rgba(239,68,68,0.06))',
              border: '1px solid var(--red)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--text-sm)', color: 'var(--red)',
            }}>
              <AlertTriangle size={12} style={{ marginRight: 6 }} />
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
